import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { postData } from '../utils/axiosRequest';
import { logout, setAuthHandler } from '../utils/createAuthProvider';
import { alertMessage } from '../utils/alertMessage';
import { useLogin } from '../hooks/useLogin';
import LoginForm, { LoginFormValues } from './LoginForm';
import getDashboardStyle from '../utils/getDashboardStyle';

/**
 * Global "session expired" re-login modal.
 *
 * Mounted once at the app root. When a request comes back unauthorized
 * (returnCode 401) the axios layer calls `requestAuth()`, which opens this
 * modal. After a successful login the new access token is stored and the
 * promise resolves `true`, so the original request is retried and the user
 * stays on the exact same page. Cancelling logs the user out and redirects to
 * the login page (the previous behaviour).
 */
const AuthModal = () => {
  const { t } = useTranslation();
  const completeLogin = useLogin();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    setAuthHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolverRef.current = resolve;
          // Prefill the username with the currently logged-in account.
          const profile = localStorage.getItem('profile');
          if (profile) {
            try {
              const { adminUserName } = JSON.parse(profile);
              if (adminUserName) {
                form.setFieldsValue({ username: adminUserName });
              }
            } catch {
              // ignore malformed profile
            }
          }
          setOpen(true);
        })
    );
    return () => setAuthHandler(null);
  }, [form]);

  /**
   * Resolve the pending auth promise exactly once.
   */
  const settle = (success: boolean) => {
    setOpen(false);
    setSubmitting(false);
    form.resetFields();
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(success);
  };

  const onFinish = async (values: LoginFormValues) => {
    setSubmitting(true);
    const response = await postData(
      'auth/login',
      getDashboardStyle().isSalesForceAppType
        ? { mobile: values.username, password: values.password }
        : { username: values.username, password: values.password }
    );

    if (response && response.goodStatus && response.data?.accessToken) {
      // Stores the token, re-fetches the profile login() cleared, and bumps
      // profileVersion so the sidebar re-renders before we resume the request.
      await completeLogin(response.data.accessToken);
      settle(true);
    } else {
      setSubmitting(false);
      alertMessage(
        'error',
        response?.msg || t('general.noResponse'),
        response?.data || undefined
      );
    }
  };

  /**
   * Cancel -> clear session and fall back to the login page.
   */
  const onCancel = () => {
    logout();
    settle(false);
    window.history.pushState({ returnCode: 401 }, '', '/auth/login');
    window.location.assign('/auth/login');
  };

  return (
    <Modal
      title={t('login.alerts.loginRequired')}
      visible={open}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      footer={null}
    >
      <LoginForm form={form} onFinish={onFinish} loading={submitting} />
    </Modal>
  );
};

export default AuthModal;
